import {useEffect, useState} from 'react';
import {Alert, Box, Card, CardActionArea, CardContent, Chip, Typography} from '@mui/material';
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined';
import {Media} from '../../../../../backend/interfaces/media';
import {MediaType} from '../../../../../backend/enums/media-type';
import {ProcessStatus} from '../../../../../backend/enums/process-status';

export interface FamilyMediaCardLabels {
  photo: string;
  video: string;
  processing: string;
  processingFailed: string;
  uploadedBy: string;
  signedUrlError: string;
  retry: string;
}

interface FamilyMediaCardProps {
  media: Media;
  ownerName: string;
  labels: FamilyMediaCardLabels;
  signedUrlError: boolean;
  onOpen: () => void;
  onImageError: () => void;
  onRetrySignedUrl: () => void;
}

const formatDate = (value?: Date | string): string => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString(undefined, {hourCycle: 'h23'});
};

export const FamilyMediaCard = ({
  media,
  ownerName,
  labels,
  signedUrlError,
  onOpen,
  onImageError,
  onRetrySignedUrl,
}: FamilyMediaCardProps) => {
  const [imageFailed, setImageFailed] = useState(false);
  const previewUrl = media.thumbnailUrl || (media.type === MediaType.Photo ? media.url : undefined);

  useEffect(() => {
    setImageFailed(false);
  }, [media, previewUrl]);

  const isVideo = media.type === MediaType.Video;
  const isProcessing = media.processingStatus === ProcessStatus.InProgress;
  const hasFailedProcessing = media.processingStatus === ProcessStatus.Failed;

  return (
    <Card className="family-media-card" variant="outlined">
      <CardActionArea
        onClick={onOpen}
        aria-label={media.description || (isVideo ? labels.video : labels.photo)}
      >
        <Box className="family-media-card-preview">
          {previewUrl && !imageFailed ? (
            <img
              className="family-media-card-image"
              src={previewUrl}
              alt={media.description || ''}
              loading="lazy"
              onError={() => {
                setImageFailed(true);
                onImageError();
              }}
            />
          ) : (
            <Box className="family-media-card-placeholder" aria-hidden="true">
              <VideoLibraryOutlinedIcon fontSize="large" />
            </Box>
          )}
          <Chip
            className="family-media-card-type"
            size="small"
            label={isVideo ? labels.video : labels.photo}
          />
          {isVideo && !media.thumbnailUrl && isProcessing && (
            <Typography className="family-media-card-processing" variant="caption">
              {labels.processing}
            </Typography>
          )}
        </Box>
      </CardActionArea>
      <CardContent className="family-media-card-content">
        {signedUrlError && (
          <Alert severity="error" sx={{mb: 1}}>
            {labels.signedUrlError}
            <Box>
              <button className="family-media-card-retry" type="button" onClick={onRetrySignedUrl}>
                {labels.retry}
              </button>
            </Box>
          </Alert>
        )}
        {hasFailedProcessing && (
          <Typography color="error" variant="caption" display="block" mb={0.5}>
            {labels.processingFailed}
          </Typography>
        )}
        {media.description && (
          <Typography className="family-media-card-description" variant="body1">
            {media.description}
          </Typography>
        )}
        {formatDate(media.createdAt) && (
          <Typography color="text.secondary" variant="caption" display="block">
            {formatDate(media.createdAt)}
          </Typography>
        )}
        <Typography color="text.secondary" variant="caption" display="block">
          {labels.uploadedBy}: {ownerName}
        </Typography>
      </CardContent>
    </Card>
  );
};
